import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/monitoring/ga4';

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * 페이지별 활성 체류시간을 측정하여 GA4에 전송한다.
 * - 탭이 보이고(focus) 있는 동안만 시간을 누적한다.
 * - 30초마다 heartbeat 이벤트를 전송한다.
 * - 페이지 이탈(라우트 변경 / pagehide) 시 최종 체류시간을 전송한다.
 */
export function useEngagementTracking(): void {
  const location = useLocation();
  const activeStartRef = useRef(Date.now());
  const accumulatedRef = useRef(0);
  const isActiveRef = useRef(!document.hidden);
  const pathRef = useRef(location.pathname);

  useEffect(() => {
    const sendEngagement = (reason: 'heartbeat' | 'route_change' | 'page_hide') => {
      if (isActiveRef.current) {
        accumulatedRef.current += Date.now() - activeStartRef.current;
        activeStartRef.current = Date.now();
      }

      const seconds = Math.round(accumulatedRef.current / 1000);
      if (seconds < 1) return;

      const params: Record<string, unknown> = {
        engagement_time_sec: seconds,
        page_path: pathRef.current,
        engagement_reason: reason,
      };

      if (reason === 'page_hide' && typeof navigator.sendBeacon === 'function') {
        trackEvent('user_engagement', params);
      } else {
        trackEvent('user_engagement', params);
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (isActiveRef.current) {
          accumulatedRef.current += Date.now() - activeStartRef.current;
          isActiveRef.current = false;
        }
      } else {
        activeStartRef.current = Date.now();
        isActiveRef.current = true;
      }
    };

    const onPageHide = () => {
      sendEngagement('page_hide');
    };

    const heartbeatId = window.setInterval(() => {
      sendEngagement('heartbeat');
    }, HEARTBEAT_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      sendEngagement('route_change');
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.clearInterval(heartbeatId);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === pathRef.current) return;

    accumulatedRef.current = 0;
    activeStartRef.current = Date.now();
    isActiveRef.current = !document.hidden;
    pathRef.current = location.pathname;
  }, [location.pathname]);
}
